import { useMutation, useQueryClient } from "@tanstack/react-query";
import clsx from "clsx";
import { enqueueSnackbar } from "notistack";
import { useState } from "react";

import { ReportRecipientList } from "~/api/Api";
import { apiClient } from "~/api/ApiClient";
import { queryKeys } from "~/api/queryKeys";
import FormLabel from "~/components/login/formLabel/FormLabel";
import MultiSelect from "~/components/mantine/multiSelect/MultiSelect";
import Select from "~/components/mantine/select/Select";
import Switch from "~/components/switch/Switch";
import Input from "~/components/ui/input/Input";
import Modal from "~/components/ui/modal/Modal";
import TextArea from "~/components/ui/textArea/TextArea";
import { useTeam } from "~/contexts/TeamProvider";
import { messageFromError } from "~/helpers/messageFromError";

import styles from "./ClientReports.module.scss";
import {
  MAX_LIST_RECIPIENTS,
  ScheduleFrequency,
  dayFor,
  dayOptions,
  joinNames,
  memberName,
  parseRecipients,
  recipientChanges,
  recipientName,
  scheduleCoverage,
} from "./delivery";
import { ReportDelivery } from "./useReportDelivery";

const FREQUENCY_OPTIONS = [
  { value: "monthly", label: "Monthly" },
  { value: "weekly", label: "Weekly" },
];

const NO_LIST = "none";
const NEW_LIST = "new";

type Props = {
  siteId: number;
  delivery: ReportDelivery;
  onClose: () => void;
};

// Mounted while open, so each opening starts from the saved settings.
const ReportDeliveryModal = ({ siteId, delivery, onClose }: Props) => {
  const { schedule, recipients, members, lists, listId } = delivery;
  const queryClient = useQueryClient();
  const { team } = useTeam();

  const [enabled, setEnabled] = useState(schedule?.enabled ?? false);
  const [frequency, setFrequency] = useState<ScheduleFrequency>(
    schedule?.frequency ?? "monthly",
  );
  const [day, setDay] = useState(schedule?.day ?? 1);
  const [selected, setSelected] = useState(() =>
    recipients.map((recipient) => recipient.membership_id),
  );
  const [listChoice, setListChoice] = useState(
    listId == null ? NO_LIST : String(listId),
  );
  const [newListName, setNewListName] = useState("");
  const [newListText, setNewListText] = useState("");

  const dayFieldLabel = frequency === "monthly" ? "Day" : "Weekday";
  const isNewList = listChoice === NEW_LIST;
  const chosenList = lists.find(({ id }) => String(id) === listChoice);
  const newListRecipients = parseRecipients(newListText);
  const hasTooManyRecipients = newListRecipients.length > MAX_LIST_RECIPIENTS;
  const isNewListReady =
    newListName.trim() !== "" &&
    newListRecipients.length > 0 &&
    !hasTooManyRecipients;
  const isEmailing = selected.length > 0 || listChoice !== NO_LIST;

  const listOptions = [
    { value: NO_LIST, label: "No list" },
    ...lists.map((list) => ({ value: String(list.id), label: list.name })),
    { value: NEW_LIST, label: "Create a new list" },
  ];

  const createList = async () => {
    const response =
      await apiClient.reportRecipientLists.reportRecipientListsCreate({
        name: newListName.trim(),
        recipients: newListRecipients,
      });
    const list = response.data.report_recipient_list;

    // Pick the saved list, so saving again after a later request fails
    // doesn't try to create it twice.
    queryClient.setQueryData<ReportRecipientList[]>(
      queryKeys.reportRecipientLists(team?.id),
      (current) => [...(current ?? []), list],
    );
    setListChoice(String(list.id));

    return list.id;
  };

  const { mutate: save, isPending } = useMutation({
    mutationFn: async () => {
      const reportRecipientListId = isNewList
        ? await createList()
        : (chosenList?.id ?? null);
      const attributes = {
        frequency,
        day,
        enabled,
        report_recipient_list_id: reportRecipientListId,
      };

      if (schedule) {
        const isChanged =
          schedule.frequency !== frequency ||
          schedule.day !== day ||
          schedule.enabled !== enabled ||
          schedule.report_recipient_list_id !== reportRecipientListId;

        if (isChanged) {
          await apiClient.sites.clientReportScheduleUpdate(siteId, attributes);
        }
      } else if (enabled || reportRecipientListId != null) {
        // The list lives on the schedule, so a paused one keeps it for the
        // reports emailed on request.
        await apiClient.sites.clientReportScheduleCreate(siteId, attributes);
      }

      const { add, remove } = recipientChanges(recipients, selected);

      await Promise.all([
        ...add.map((membershipId) =>
          apiClient.sites.clientReportRecipientsCreate(siteId, {
            membership_id: membershipId,
          }),
        ),
        ...remove.map((id) =>
          apiClient.sites.clientReportRecipientsDelete(siteId, id),
        ),
      ]);
    },
    onSuccess: () => {
      enqueueSnackbar("Delivery settings saved", { variant: "success" });
      onClose();
    },
    onError: (err) => {
      enqueueSnackbar(messageFromError(err), {
        variant: "error",
        persist: true,
      });
    },
    // Some of the requests may have gone through before one failed.
    onSettled: () =>
      Promise.all([
        queryClient.invalidateQueries({
          queryKey: queryKeys.clientReportSchedule(siteId),
        }),
        queryClient.invalidateQueries({
          queryKey: queryKeys.clientReportRecipients(siteId),
        }),
        queryClient.invalidateQueries({
          queryKey: queryKeys.reportRecipientLists(team?.id),
        }),
      ]),
  });

  return (
    <Modal
      isOpen
      closeModal={onClose}
      modalLabel={<h3>Report Delivery</h3>}
      buttonLabel="Save"
      action={() => save()}
      isLoading={isPending}
      isDisabled={isPending || (isNewList && !isNewListReady)}
    >
      <div className={styles.deliveryForm}>
        <section className={styles.deliverySection}>
          <Switch
            label="Create a report on a schedule"
            checked={enabled}
            onChange={(event) => setEnabled(event.currentTarget.checked)}
          />
          <div className={styles.scheduleFields}>
            <div>
              <FormLabel label="Frequency" />
              <Select
                aria-label="Frequency"
                data={FREQUENCY_OPTIONS}
                value={frequency}
                onChange={(value) => {
                  if (!value) return;

                  const next = value as ScheduleFrequency;

                  setFrequency(next);
                  setDay(dayFor(next, day));
                }}
                searchable={false}
                allowDeselect={false}
                disabled={!enabled}
              />
            </div>
            <div>
              <FormLabel label={dayFieldLabel} />
              <Select
                aria-label={dayFieldLabel}
                data={dayOptions(frequency)}
                value={String(day)}
                onChange={(value) => value && setDay(Number(value))}
                searchable={false}
                allowDeselect={false}
                disabled={!enabled}
              />
            </div>
          </div>
          <p className={styles.deliveryHint}>
            Each report covers {scheduleCoverage(frequency)}.
          </p>
        </section>

        <section className={styles.deliverySection}>
          <h4 className={styles.deliveryHeading}>Email reports to</h4>
          <p className={styles.deliveryHint}>
            {isEmailing
              ? "They get scheduled reports, and the ones you choose to email when creating a report."
              : "With nobody chosen, reports only appear on this page."}
          </p>

          <div className={styles.deliveryFields}>
            <div className={styles.deliveryField}>
              <FormLabel label="Team members" />
              <MultiSelect
                aria-label="Team members"
                data={members.map((membership) => ({
                  value: String(membership.id),
                  label: memberName(membership),
                }))}
                value={selected.map(String)}
                onChange={(values) => setSelected(values.map(Number))}
                placeholder={
                  selected.length ? undefined : "Choose team members"
                }
                clearable
              />
              <p className={styles.deliveryHint}>
                Only team members with access to this site are listed.
              </p>
            </div>

            <div className={styles.deliveryField}>
              <FormLabel label="Recipient list" />
              <Select
                aria-label="Recipient list"
                data={listOptions}
                value={listChoice}
                onChange={(value) => value && setListChoice(value)}
                searchable={lists.length > 8}
                allowDeselect={false}
              />
              {chosenList && (
                <p className={styles.deliveryHint}>
                  {joinNames(chosenList.recipients.map(recipientName), 3)}.
                  Lists are shared by every site on the team.
                </p>
              )}
              {listChoice === NO_LIST && (
                <p className={styles.deliveryHint}>
                  A list holds any email addresses, such as your client&apos;s,
                  and can be reused for other sites.
                </p>
              )}
            </div>

            {isNewList && (
              <>
                <div className={styles.deliveryField}>
                  <FormLabel label="List name" />
                  <Input
                    aria-label="List name"
                    value={newListName}
                    onChange={(event) => setNewListName(event.target.value)}
                    placeholder="Acme marketing team"
                  />
                </div>
                <div className={styles.deliveryField}>
                  <FormLabel label="Addresses" />
                  <TextArea
                    aria-label="Addresses"
                    value={newListText}
                    onChange={(event) => setNewListText(event.target.value)}
                    minRows={4}
                    maxRows={10}
                    placeholder={
                      'dana@example.com\n"Fox Mulder" <fox@example.com>'
                    }
                  />
                  <p
                    className={clsx(
                      styles.deliveryHint,
                      hasTooManyRecipients && styles.deliveryHintError,
                    )}
                  >
                    {hasTooManyRecipients
                      ? `${newListRecipients.length} addresses: a list can have at most ${MAX_LIST_RECIPIENTS}.`
                      : `One per line, up to ${MAX_LIST_RECIPIENTS}. ${newListRecipients.length} so far.`}
                  </p>
                </div>
              </>
            )}
          </div>
        </section>
      </div>
    </Modal>
  );
};

export default ReportDeliveryModal;
